import { z } from 'zod';
import { OperatorRoleSchema } from './enums.js';

// Operator row Zod schema. The front uses this on signin to render the
// header (name, role badge, timezone) AND to gate UI elements by role
// (config.update buttons hidden for non-admins, etc.).
//
// We intentionally split the row in two:
//   - OperatorProfileSchema  : what the front needs every render
//   - OperatorConfigSchema   : per-operator preferences (kelly fraction,
//     bankroll caps, exposure limits, edge thresholds). Used by the bet-
//     sizing calculator on the operator detail page.
//
// Why split: the profile is hot-path (every page load); the config is
// admin-only (rarely read by the operator's own UI). Different cache
// strategies on the front.

export const OperatorProfileSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  // null when operator is a service/system account; for human ops this
  // matches auth.users.id (Supabase Auth).
  userId: z.string().uuid().nullable(),
  name: z.string().min(1).max(120),
  role: OperatorRoleSchema,
  isActive: z.boolean(),
  // IANA tz, used only for UI rendering. All wire timestamps stay UTC.
  timezone: z.string(),
  language: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type OperatorProfile = z.infer<typeof OperatorProfileSchema>;

// Per-operator betting config — feeds the bet-sizing calculator + risk gates
// in the UI. All numeric fields stored as Postgres numeric; on the wire we
// expose them as numbers (the route coerces from strings).
//
// V1 NOTE: most of these fields are populated with safe defaults during the
// signup flow (when there's a signup flow). Until then, every field has a
// schema-level default in DB; the UI should treat null/undefined as "use
// global default" rather than blocking.
export const OperatorConfigSchema = z.object({
  // ── Bankroll ────────────────────────────────────────────────────────────
  bankrollTotalUsd: z.number().nonnegative().nullable(),
  // { [bookSlug]: number } — per-book bankroll allocation
  bankrollPerBook: z.record(z.number().nonnegative()).nullable(),
  // ── Kelly ──────────────────────────────────────────────────────────────
  kellyFraction: z.number().min(0).max(1),
  kellyFractionDrawdown: z.number().min(0).max(1),
  drawdownThresholdPct: z.number().min(0).max(100),
  varianceAdjustment: z.enum(['none', 'simple', 'confidence_weighted']),
  // ── Stake caps (% of bankroll) ─────────────────────────────────────────
  maxStakePerBetPct: z.number().min(0).max(100),
  minStakeUsd: z.number().nonnegative(),
  maxExposurePerMatchPct: z.number().min(0).max(100),
  maxExposurePerPlayerPct: z.number().min(0).max(100),
  maxExposurePerDayPct: z.number().min(0).max(100),
  maxExposurePerBookPct: z.number().min(0).max(100),
  // ── Edge detection ─────────────────────────────────────────────────────
  edgeMode: z.enum(['raw', 'no_vig', 'vs_sharp']),
  minEdgePct: z.number().min(0).max(1),
  minOdds: z.number().nullable(),
  maxOdds: z.number().nullable(),
  allowedBooks: z.array(z.string()).nullable(),
  disabledBooks: z.array(z.string()).nullable(),
  allowedSurfaces: z.array(z.string()).nullable(),
  // ── Risk policies ──────────────────────────────────────────────────────
  onSharpDisagreement: z.enum(['block', 'reduce_50', 'warn_only']),
  retirementRiskHaircutPct: z.number().min(0).max(100),
  serveProbDriftAction: z.enum(['warn_only', 'auto_adjust_to_observed', 'pause_alerts_match']),
});
export type OperatorConfig = z.infer<typeof OperatorConfigSchema>;

// Full operator row = profile + config. Use this when the operator detail
// page loads. For most pages, OperatorProfileSchema alone is enough.
export const OperatorRowSchema = OperatorProfileSchema.merge(OperatorConfigSchema);
export type OperatorRow = z.infer<typeof OperatorRowSchema>;

// Returned by the apps/api `me` endpoint. Front renders header + role badge
// from `operator`; the bet-sizing settings page reads `config`.
export const MeResponseSchema = z.object({
  operator: OperatorProfileSchema,
  // Full config only included when the requester is admin OR is the same
  // operator. Junior/senior viewing each other only see profile.
  config: OperatorConfigSchema.nullable(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

// PATCH input for operators.updateMyConfig. Same semantics as
// ScannerConfigPatchSchema: any subset of OperatorConfig fields, must contain
// at least one. The route applies them with a single UPDATE … SET … WHERE
// id = self. Profile fields (name, timezone, language) live alongside config
// in the same partial — settings UI typically sets them together.
export const OperatorConfigPatchSchema = z
  .object({
    name: z.string().min(1).max(120).optional(),
    timezone: z.string().min(1).max(64).optional(),
    language: z.string().min(2).max(8).optional(),
  })
  .merge(OperatorConfigSchema.partial())
  .refine((o) => Object.keys(o).length > 0, {
    message: 'PATCH must contain at least one field',
  });
export type OperatorConfigPatch = z.infer<typeof OperatorConfigPatchSchema>;
